import React, { useEffect, useState } from "react";
import { Sidebar } from "@excalidraw/excalidraw/components/Sidebar/Sidebar";
import clsx from "clsx";
import { withInternalFallback } from "@excalidraw/excalidraw/components/hoc/withInternalFallback";
import type { SidebarProps, SidebarTriggerProps } from "@excalidraw/excalidraw/components/Sidebar/common";
import { ImageIcon } from "@excalidraw/excalidraw/components/icons";
import { useExcalidrawSetAppState } from "@excalidraw/excalidraw/components/App";
import { composeEventHandlers } from "@excalidraw/common";
import { t } from "@excalidraw/excalidraw/i18n";
import type { Merge, MarkOptional } from "@excalidraw/common/utility-types";
import "./DrawingsSidebar.scss";

export const DRAWINGS_SIDEBAR = {
    name: "drawings" as const,
    defaultTab: "drawings" as const,
};

interface DrawingItem {
    _id: string; // MongoDB ID 
    userId: string;
    drawingData: any;
    name?: string;
    createdAt: string;
}

interface DrawingsSidebarProps {
    fetchDrawings: () => Promise<any[]>;
    loadDrawing: (drawing: any) => void;
    deleteDrawing?: (drawingId: string) => Promise<boolean>;
    renameDrawing?: (drawingId: string, name: string) => Promise<boolean>;
    createNewDrawing?: () => void;
}

const DrawingsList = ({
    drawings,
    loadDrawing,
    deleteDrawing,
    renameDrawing,
    onRefresh
}: {
    drawings: DrawingItem[];
    loadDrawing: (drawing: any) => void;
    deleteDrawing?: (drawingId: string) => Promise<boolean>;
    renameDrawing?: (drawingId: string, name: string) => Promise<boolean>;
    onRefresh?: () => void;
}) => {
    console.log("Rendering DrawingsList with drawings:", drawings.map(d => d._id));
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [newName, setNewName] = useState<string>('');

    if (!drawings.length) {
        return <div className="no-drawings">No saved drawings found</div>;
    }

    const handleDelete = async (e: React.MouseEvent, drawingId: string) => {
        e.stopPropagation(); // Prevent triggering the loadDrawing onClick
        setPendingDeleteId(drawingId);
    };

    const confirmDelete = async () => {
        if (pendingDeleteId && deleteDrawing) {
            try {
                const success = await deleteDrawing(pendingDeleteId);
                if (success && onRefresh) {
                    onRefresh(); // Trigger a refresh in parent component
                }
                setPendingDeleteId(null);
            } catch (error) {
                console.error("Error deleting drawing:", error);
                setPendingDeleteId(null);
            }
        }
    };

    const cancelDelete = () => {
        setPendingDeleteId(null);
    };

    const handleRename = (e: React.MouseEvent, drawingId: string, currentName: string) => {
        e.stopPropagation(); // Prevent triggering the loadDrawing onClick
        setEditingId(drawingId);
        setNewName(currentName || 'Untitled Drawing');
    };

    const confirmRename = async () => {
        if (editingId && renameDrawing && newName.trim()) {
            try {
                const success = await renameDrawing(editingId, newName.trim());
                if (success && onRefresh) {
                    onRefresh(); // Trigger a refresh in parent component
                }
            } catch (error) {
                console.error("Error renaming drawing:", error);
            } finally {
                setEditingId(null);
                setNewName('');
            }
        }
    };

    const cancelRename = () => {
        setEditingId(null);
        setNewName('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            confirmRename();
        } else if (e.key === 'Escape') {
            cancelRename();
        }
    };

    return (
        <div className="drawings-list">
            {drawings.map((drawing) => {
                const drawingId = drawing._id;
                const displayName = drawing.name || 'Untitled Drawing';
                return (
                    <div
                        key={drawingId}
                        className="drawing-item"
                        onClick={() => loadDrawing(drawing)}
                    >
                        <div className="drawing-info">
                            {editingId === drawingId ? (
                                <div className="drawing-name-input-container">
                                    <label htmlFor={`drawing-name-${drawingId}`} className="sr-only">
                                        Drawing name
                                    </label>
                                    <input
                                        id={`drawing-name-${drawingId}`}
                                        type="text"
                                        className="drawing-name-input"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                        aria-label="Drawing name"
                                        placeholder="Enter drawing name"
                                    />
                                </div>
                            ) : (
                                <div className="drawing-name">
                                    {displayName}
                                </div>
                            )}
                            <div className="drawing-date">
                                {new Date(drawing.createdAt).toLocaleString()}
                            </div>
                        </div>
                        <div className="drawing-actions">
                            {renameDrawing && editingId === drawingId ? (
                                <div className="rename-buttons">
                                    <button
                                        className="rename-confirm-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            confirmRename();
                                        }}
                                        title="Save"
                                    >
                                        ✓
                                    </button>
                                    <button
                                        className="rename-cancel-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            cancelRename();
                                        }}
                                        title="Cancel"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {renameDrawing && (
                                        <button
                                            className="drawing-rename-btn"
                                            onClick={(e) => handleRename(e, drawingId, displayName)}
                                            title="Rename drawing"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                        </button>
                                    )}
                                    {deleteDrawing && (
                                        <button
                                            className="drawing-delete-btn"
                                            onClick={(e) => handleDelete(e, drawingId)}
                                            title="Delete drawing"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                                                <line x1="10" y1="11" x2="10" y2="17" />
                                                <line x1="14" y1="11" x2="14" y2="17" />
                                            </svg>
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                        {pendingDeleteId === drawingId && (
                            <div className="delete-confirmation">
                                <div className="delete-confirmation-message">
                                    Delete this drawing?
                                </div>
                                <div className="delete-confirmation-buttons">
                                    <button
                                        className="delete-confirm-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            confirmDelete();
                                        }}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        className="delete-cancel-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            cancelDelete();
                                        }}
                                    >
                                        No
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const DrawingsSidebarTrigger = (
    props: Omit<SidebarTriggerProps, "name"> &
        React.HTMLAttributes<HTMLDivElement>,
) => {
    return (
        <Sidebar.Trigger
            {...props}
            className="drawings-sidebar-trigger"
            name={DRAWINGS_SIDEBAR.name}
            icon={ImageIcon}
        >
            {props.children || "Drawings"}
        </Sidebar.Trigger>
    );
};
DrawingsSidebarTrigger.displayName = "DrawingsSidebarTrigger";

export const DrawingsSidebarComponent = ({
    fetchDrawings,
    loadDrawing,
    deleteDrawing,
    renameDrawing,
    createNewDrawing,
    children,
    className,
    onDock,
    docked,
    ...rest
}: Merge<
    MarkOptional<Omit<SidebarProps, "name">, "children">,
    {
        /** pass `false` to disable docking */
        onDock?: SidebarProps["onDock"] | false;
    } & DrawingsSidebarProps
>) => {
    const [drawings, setDrawings] = useState<DrawingItem[]>([]);
    const [filteredDrawings, setFilteredDrawings] = useState<DrawingItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const setAppState = useExcalidrawSetAppState();

    const loadDrawings = async () => {
        setIsLoading(true);
        try {
            const fetchedDrawings = await fetchDrawings();
            setDrawings(fetchedDrawings || []);
        } catch (error) {
            console.error("Error loading drawings:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Trigger refresh when refreshCounter changes
    useEffect(() => {
        loadDrawings();
    }, [fetchDrawings, refreshCounter]);

    // Filter drawings based on search term and sort by time
    useEffect(() => {
        // First filter the drawings based on search term
        let filtered = !searchTerm.trim()
            ? [...drawings]
            : drawings.filter(drawing =>
                (drawing.name || 'Untitled Drawing').toLowerCase().includes(searchTerm.toLowerCase())
            );

        // Then sort filtered drawings by time (newest first)
        filtered = filtered.sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

        setFilteredDrawings(filtered);
    }, [drawings, searchTerm]);

    // Function to pass to DrawingsList for refreshing
    const handleRefresh = () => {
        setRefreshCounter(prev => prev + 1);
    };

    return (
        <Sidebar
            {...rest}
            name={DRAWINGS_SIDEBAR.name}
            className={clsx("drawings-sidebar", className)}
            docked={docked}
            onDock={
                onDock === false
                    ? undefined
                    : composeEventHandlers(onDock, (docked) => {
                        setAppState({ defaultSidebarDockedPreference: docked });
                    })
            }
        >
            <Sidebar.Header>
                <span>Saved Drawings</span>
                <div style={{ display: "flex", gap: "8px" }}>
                    {createNewDrawing && (
                        <button
                            onClick={createNewDrawing}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "4px",
                                borderRadius: "4px",
                            }}
                            title="New drawing"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                        </button>
                    )}
                    <button
                        onClick={loadDrawings}
                        disabled={isLoading}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "4px",
                            borderRadius: "4px",
                        }}
                        title="Refresh drawings"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38" />
                        </svg>
                    </button>
                </div>
            </Sidebar.Header>
            <div className="drawings-search-container" style={{ padding: "0 0.8rem", marginBottom: "10px" }}>
                <div className="drawings-search">
                    <input
                        type="text"
                        placeholder="Search drawings..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="drawings-search-input"
                    />
                    {searchTerm && (
                        <button
                            className="drawings-search-clear"
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>
            <div className="drawings-sidebar-content">
                {isLoading ? (
                    <div className="drawings-loading">Loading drawings...</div>
                ) : (
                    <DrawingsList
                        drawings={filteredDrawings}
                        loadDrawing={loadDrawing}
                        deleteDrawing={deleteDrawing}
                        renameDrawing={renameDrawing}
                        onRefresh={handleRefresh}
                    />
                )}
            </div>
            {children}
        </Sidebar>
    );
};

export const DrawingsSidebar = Object.assign(DrawingsSidebarComponent, {
    Trigger: DrawingsSidebarTrigger,
}); 